import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { message } from "antd";
import MainWrapper from "../../components/MainWrapper";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import SuccessMessage from "../../components/SuccessMessage";
import SpinerIndicatorWhiteBg from "../../components/SpinerIndicatorWhiteBg";
import { checkSgdTransaction } from "../../redux/actions/postQrInitManual";
import PayNowLogo from "../../assets/PAYNOWlogo.png";
import expiredQr from "../../assets/expiredQr_eng.png";
import { toLocaleStringFormate } from "../../helpingFunctions";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width:'100%';
//   position: absolute;
`;

const CardWrapper = styled.div`
  margin-top: 50px; 
  margin-bottom: 20px;
  width: 380px;
  background-color: white;
  border-radius: 15px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

  @media (max-width: 400px) {
    width: 90%;
  }

  .logo-container {
    margin-bottom: 20px;
    width: 150px;
    img {
      width: 100%;
    }
  }

  .qr-container {
    border: 8px solid #7c217e;
    border-radius: 12px;
    padding: 10px;
    width: 100%;
    margin-bottom: 12px;
    position: relative;
  }

  .qr-footer {
    background-color: #7c217e;
    color: white;
    text-align: center;
    padding: 10px;
    font-weight: bold;
    margin: 10px -10px -10px -10px;
    border-radius: 0 0 4px 4px;
    font-size: 1rem;
  }

  .instructions {
    text-align: center;
    font-size: 12px;
    color: #333;
    margin-bottom: 16px;
  }

  .ref-id {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }

  .amount {
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .expiry {
    color: #7c217e;
    font-size: 0.8rem;
    margin-bottom: 5px;
  }

  .timer {
    color: #7c217e;
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .error {
    color: red;
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .footer-text {
    font-size: 12px;
    text-align: center;
    color: #333;
    
    a {
      color: #333;
      text-decoration: underline;
      font-weight: bold;
    }
  }
`;

const SgdQrCardInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pollingRef = useRef(null);

    const { data: verifyData } = useSelector((state) => state.getVerifyManualTransactionReducer);
    const { data: qrInitData, isLoading: qrInitLoading, error } = useSelector((state) => state.postQrInitManualReducer);
    const { data: trnxCheckData } = useSelector((state) => state.getQrManualCheckReducer);

    const [timeLeft, setTimeLeft] = useState(600);
    const [isPaid, setIsPaid] = useState(false);

    // useEffect(() => {
    //     if (!verifyData) {
    //         navigate(`/sgd/${params.id}`);
    //     }
    // }, [verifyData]);

    useEffect(() => {
        if (error || qrInitLoading || !qrInitData || !verifyData?.trxRef) return;

        const expiryKey = `sgd_qr_expiry_${verifyData.trxRef}`;
        let expiryTime = localStorage.getItem(expiryKey);

        if (!expiryTime) {
            expiryTime = Date.now() + 600 * 1000;
            localStorage.setItem(expiryKey, expiryTime);
        } else {
            expiryTime = parseInt(expiryTime, 10);
        }

        const updateTimer = () => {
            const now = Date.now();
            const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
            setTimeLeft(remaining);

            if (remaining === 0) {
                localStorage.removeItem(expiryKey);
            }
        };

        updateTimer();
        const timer = setInterval(updateTimer, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [error, qrInitLoading, qrInitData, verifyData?.trxRef]);

    useEffect(() => {
        const status = trnxCheckData?.status;
        if (!verifyData?.trxRef || isPaid || status === "03_Rejected" || status === "FAILED" || timeLeft === 0) {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }

        const pollStatus = () => {
            dispatch(checkSgdTransaction(verifyData.trxRef));
        };

        if (!pollingRef.current) {
            pollStatus();
            pollingRef.current = setInterval(pollStatus, 5000);
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [verifyData, isPaid, timeLeft === 0, trnxCheckData?.status, dispatch]);

    useEffect(() => {
        if (trnxCheckData?.status === "PAID" || trnxCheckData?.status === "02_Paid" || trnxCheckData?.status === "SUCCESS") {
            setIsPaid(true);
            if (pollingRef.current) clearInterval(pollingRef.current);

            setTimeout(() => {
                const expiryKey = `sgd_qr_expiry_${verifyData?.trxRef}`;
                localStorage.removeItem(expiryKey);

                const redirectUrl = localStorage.getItem("redirectionUrl") || verifyData?.redirectUrl || verifyData?.siteUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
            }, 5000);
        }
    }, [trnxCheckData, verifyData]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <Container>
            {qrInitLoading ? <SpinerIndicatorWhiteBg /> :
                <CardWrapper>
                    <div className="logo-container">
                        <img src={PayNowLogo} alt="PayNow SGD" />
                    </div>

                    {isPaid ? (
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            <SuccessMessage />
                            <h3 style={{ color: "green", marginTop: 20 }}>Payment Successful</h3>
                            <p>Redirecting you back...</p>
                        </div>
                    ) : (
                        <>
                            <div className="qr-container">
                                {!qrInitData?.qrCode || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ? (
                                    <img src={expiredQr} alt="Expired" width="100%" />
                                ) : (
                                    <img
                                        src={qrInitData?.qrCode?.startsWith('data:') ? qrInitData?.qrCode : `data:image/png;base64,${qrInitData?.qrCode}`}
                                        alt="Payment QR"
                                        width="100%"
                                    />
                                )}
                                <div className="qr-footer">SINGAPORE PAYNOW QR</div>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                {error || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ? null :
                                    <p className="instructions">
                                        Scan the QR code using your bank app to complete the payment
                                    </p>
                                }

                                <div className="ref-id">REF: {verifyData?.trxRef}</div>
                                <div className="amount">SGD {toLocaleStringFormate(verifyData?.amount)}</div>

                                {error || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ?
                                    <p className="error">
                                        {error ? error.split(':')[1] : "Transaction Expired"}
                                    </p>
                                    :
                                    <div>
                                        <div className="expiry">QR Code expires in</div>
                                        <div className="timer">{formatTime(timeLeft)}</div>
                                    </div>
                                }

                                <div className="footer-text">
                                    Click <a href={verifyData?.redirectUrl || verifyData?.siteUrl}>here</a> if you’re not redirected back to the merchant page after successful payment
                                </div>
                            </div>
                        </>
                    )}
                </CardWrapper>
            }
        </Container>
    );
};

export default SgdQrCardInfo;
